"use client";

import { Icon } from "@/components/library/Icon";
import {
  asIconName,
  asIconType,
  joinClasses,
  SECTION_BG,
} from "@/puck/landing/shared";
import type {
  LpFeatureStripBlockProps,
  LpHowItWorksBlockProps,
  LpReframeBlockProps,
  LpWorkflowsBlockProps,
} from "@/puck/types";

export function LpFeatureStripBlock({
  items,
}: Readonly<LpFeatureStripBlockProps>) {
  return (
    <section className="w-full">
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
        {items.map((feature, index) => (
          <div
            key={`${feature.text}-${index}`}
            className={joinClasses(
              "px-6 py-10",
              index > 0 && "md:border-l md:border-black/10",
            )}
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gail-primary-light text-gail-primary">
              <Icon
                icon={asIconName(feature.iconName)}
                type={asIconType(feature.iconType)}
                className="h-5 w-5"
              />
            </span>
            <p className="mt-4 font-monument text-sm leading-snug text-black md:text-[15px]">
              {feature.text}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function LpReframeBlock({
  eyebrow,
  titleLead,
  titleAccent,
  cards,
}: Readonly<LpReframeBlockProps>) {
  return (
    <section className="w-full bg-gail-primary px-4 py-20 md:py-28">
      <div className="mx-auto w-full max-w-6xl">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-white">
          {eyebrow}
        </p>
        <h2 className="mt-5 max-w-3xl font-monument text-4xl font-bold leading-[1.08] tracking-tight text-white md:text-6xl">
          {titleLead} <span className="text-white">{titleAccent}</span>
        </h2>

        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((card, index) => (
            <div
              key={`card-${index}`}
              className="rounded-2xl border border-white/15 bg-white/5 px-6 py-6"
            >
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-white">
                {card.label}
              </p>
              <p className="mt-4 font-monument text-base leading-snug text-white md:text-lg">
                {card.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function LpHowItWorksBlock({
  eyebrow,
  titleLead,
  titleAccent,
  subtitle,
  steps,
}: Readonly<LpHowItWorksBlockProps>) {
  return (
    <section className="w-full px-4 pt-20 pb-20 md:pt-28 md:pb-28">
      <div className="mx-auto w-full max-w-3xl text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-gail-primary">
          {eyebrow}
        </p>
        <h2 className="mt-5 font-monument text-4xl font-bold leading-[1.08] tracking-tight text-black md:text-6xl">
          {titleLead} <span className="text-gail-primary">{titleAccent}</span>
        </h2>
        <p className="mx-auto mt-6 max-w-xl font-monument text-base leading-relaxed text-gail-gray md:text-lg">
          {subtitle}
        </p>
      </div>

      <div className="mx-auto mt-12 grid w-full max-w-6xl grid-cols-1 gap-4 md:mt-16 md:grid-cols-3">
        {steps.map((step, index) => (
          <div
            key={`step-${index}`}
            className="flex flex-col rounded-2xl border border-black/10 bg-white px-6 py-6 md:px-7 md:py-7"
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-gail-primary">
              {step.step}
            </p>
            <h3 className="mt-4 font-monument text-xl font-bold leading-snug tracking-tight text-black">
              {step.title}
            </h3>
            <p className="mt-3 font-monument text-sm leading-relaxed text-gail-gray md:text-[15px]">
              {step.description}
            </p>
            <p className="mt-6 border-t border-black/10 pt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-gail-gray md:mt-auto">
              {step.timeline}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function LpWorkflowsBlock({
  eyebrow,
  title,
  subtitle,
  background,
  topBorder,
  items,
}: Readonly<LpWorkflowsBlockProps>) {
  return (
    <section
      className={joinClasses(
        "w-full px-4 pt-20 pb-20 md:pt-28 md:pb-28",
        topBorder && "border-t border-black/10",
        SECTION_BG[background],
      )}
    >
      <div className="mx-auto w-full max-w-3xl text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-gail-primary">
          {eyebrow}
        </p>
        <h2 className="mt-5 font-monument text-4xl font-bold leading-[1.08] tracking-tight text-black md:text-6xl">
          {title}
        </h2>
        <p className="mx-auto mt-6 max-w-xl font-monument text-base leading-relaxed text-gail-gray md:text-lg">
          {subtitle}
        </p>
      </div>

      <div className="mx-auto mt-12 grid w-full max-w-6xl grid-cols-1 gap-4 md:mt-16 md:grid-cols-2 lg:grid-cols-4">
        {items.map((workflow, index) => (
          <div
            key={`workflow-${index}`}
            className="flex flex-col rounded-2xl border border-black/10 bg-white px-6 py-6 md:px-7 md:py-7"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gail-primary-light text-gail-primary">
              <Icon
                icon={asIconName(workflow.iconName)}
                type={asIconType(workflow.iconType)}
                className="h-5 w-5"
              />
            </span>
            <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.18em] text-gail-primary">
              {workflow.number} · {workflow.label}
            </p>
            <h3 className="mt-3 font-monument text-xl font-bold leading-snug tracking-tight text-black">
              {workflow.title}
            </h3>
            <p className="mt-3 font-monument text-sm leading-relaxed text-gail-gray md:text-[15px]">
              {workflow.description}
            </p>
            <div className="mt-6 flex flex-wrap gap-2 border-t border-black/10 pt-4 md:mt-auto">
              {workflow.channels.map((channel, channelIndex) => (
                <span
                  key={`channel-${channelIndex}`}
                  className="rounded-md bg-black/5 px-2.5 py-1 font-mono text-[11px] text-gail-gray"
                >
                  {channel.text}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
